import React from "react";
import "./styles.css";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { InputLabel, IconButton } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import ClearIcon from "@mui/icons-material/Close";

export interface MonthYearPickerProps {
  label?: string;
  required?: boolean;
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  shouldDisableDate?: any;
  showClearIcon?: boolean;
  placeholder?: string;
  disableTyping?: boolean;
}

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  label,
  required = false,
  value,
  onChange,
  shouldDisableDate,
  showClearIcon = true,
  placeholder,
  disableTyping = false,
}) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "9px",
      }}
    >
      {label && (
        <InputLabel>
          {label}
          {required ? " *" : ""}
        </InputLabel>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          views={["year", "month"]}
          openTo="month"
          minDate={dayjs("2000-01-01")}
          maxDate={dayjs()}
          value={value}
          onChange={onChange}
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            textField: (params) => {
              const handleKeyDown = (
                e: React.KeyboardEvent<HTMLInputElement>,
              ) => {
                if (disableTyping) {
                  const allowed =
                    e.key === "Tab" ||
                    e.key === "ArrowLeft" ||
                    e.key === "ArrowRight" ||
                    e.key === "ArrowUp" ||
                    e.key === "ArrowDown" ||
                    e.key === "Enter" ||
                    e.key === "Escape";

                  if (!allowed) e.preventDefault();
                }
              };

              const handlePaste = (
                e: React.ClipboardEvent<HTMLInputElement>,
              ) => {
                if (disableTyping) e.preventDefault();
              };

              return {
                ...params,
                placeholder: placeholder ?? "Select month & year",
                fullWidth: true,
                variant: "outlined",
                InputLabelProps: { shrink: true },
                inputProps: {
                  ...params.inputProps,
                  readOnly: disableTyping,
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    params.inputProps?.onKeyDown?.(e);
                    handleKeyDown(e);
                  },
                  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                    params.inputProps?.onPaste?.(e);
                    handlePaste(e);
                  },
                },

                InputProps: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps?.endAdornment}
                      {showClearIcon && value && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange(null);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </>
                  ),
                },

                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    gap: "8px",
                  },
                },
              };
            },

            desktopPaper: {
              sx: {
                "& .MuiPickersYear-yearButton.Mui-disabled": {
                  pointerEvents: "none !important",
                  opacity: 0.4,
                  color: "#9e9e9e",
                },
                "& .MuiPickersMonth-monthButton.Mui-disabled": {
                  pointerEvents: "none !important",
                  opacity: 0.4,
                  color: "#9e9e9e",
                },
                "& .MuiPickersYear-yearButton.Mui-disabled:hover": {
                  backgroundColor: "transparent !important",
                  cursor: "default !important",
                },
                "& .MuiPickersMonth-monthButton.Mui-disabled:hover": {
                  backgroundColor: "transparent !important",
                  cursor: "default !important",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
};

export default MonthYearPicker;
